
// components/HealthAssessment/MenopauseCareIntent.jsx

import React, {
  useMemo,
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';

import styles from '../../styles/assessments.module.css';
import {
  ScreenHeader,
  ProgressBar,
  ScreenFooter,
  Check,
  ChevronRight,
  ChevronLeft,
} from './SharedUI';

// ─── Inline scroll-to-bottom (removes ../../hooks dep) ───
const useScrollToBottom = () => {
  const scrollRef   = useRef(null);
  const [showButton, setShowButton] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      setShowButton(el.scrollHeight - el.scrollTop - el.clientHeight > 80);
    };
    el.addEventListener('scroll', onScroll);
    onScroll();
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  const scrollToBottom = useCallback(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, []);

  return { scrollRef, showButton, scrollToBottom };
};

// ─── Inline scroll button (removes ../common dep) ─────────
const ScrollToBottomButton = ({ onClick }) => (
  <button
    className={styles.scrollToBottomBtn}
    onClick={onClick}
    aria-label="Scroll to bottom"
  >
    <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  </button>
);

// ─── Data ─────────────────────────────────────────────────
const INTENT_OPTIONS = [
  { label: 'Manage symptoms',               icon: '👤' },
  { label: 'Hormone therapy consultation',  icon: '🌿' },
  { label: 'Natural / lifestyle approaches',icon: '🌱' },
  { label: 'Mental well-being support',     icon: '🧠' },
  { label: 'Sleep improvement',             icon: '🌙' },
  { label: 'Weight management',             icon: '⚖️' },
  { label: 'Bone & heart health protection',icon: '❤️' },
  { label: 'Sexual health support',         icon: '💜' },
  { label: 'General guidance & education',  icon: '📖' },
];

const LIFESTYLE_OPTIONS  = ['Yes, please', 'Not now', 'Not interested'];
const HORMONE_OPTIONS    = ["Yes, I'd like to know more", 'Maybe later', 'No, not interested'];
const CONTINUE_OPTIONS   = [
  'Book a consult with a specialist',
  'Join a menopause care program',
  'Get a personalized care plan',
  'Chat with our care team',
];

// ─────────────────────────────────────────────────────────
const MenopauseCareIntent = ({ data, onChange, onSubmit, onBack, onExit }) => {
  const { scrollRef, showButton, scrollToBottom } = useScrollToBottom();

  const QUESTIONS = useMemo(() => ['intent', 'lifestyle', 'hormone', 'continue'], []);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const isLastQuestion = currentQuestion === QUESTIONS.length - 1;

  // ── Intent multi-select ──────────────────────────────────
  const selectedIntents = data.careIntents || [];
  const toggleIntent = (label) => {
    onChange(
      'careIntents',
      selectedIntents.includes(label)
        ? selectedIntents.filter((x) => x !== label)
        : [...selectedIntents, label]
    );
  };

  // ── Navigation ───────────────────────────────────────────
  const scrollTop = () => scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });

  const handleNext = () => {
    if (!isLastQuestion) { setCurrentQuestion((p) => p + 1); scrollTop(); return; }
    onSubmit?.();
  };

  const handleBack = () => {
    if (currentQuestion > 0) { setCurrentQuestion((p) => p - 1); scrollTop(); return; }
    onBack?.();
  };

  // ── Question counter label ────────────────────────────────
  const Counter = () => (
    <div className={styles.questionCounter}>
      Question {currentQuestion + 1} of {QUESTIONS.length}
    </div>
  );

  // ── Three-option row ──────────────────────────────────────
  const ThreeRow = ({ field, options }) => (
    <div className={styles.threeOptions}>
      {options.map((opt) => (
        <button
          key={opt}
          className={`${styles.threeOption} ${data[field] === opt ? styles.threeOptionSelected : ''}`}
          onClick={() => onChange(field, opt)}
        >
          {data[field] === opt && (
            <span className={styles.threeOptionDot}><Check /></span>
          )}
          {opt}
        </button>
      ))}
    </div>
  );

  // ── Render active question ────────────────────────────────
  const renderQuestion = () => {
    const type = QUESTIONS[currentQuestion];

    // Q1 — Intent grid
    if (type === 'intent') {
      return (
        <div className={styles.questionBlock}>
          <Counter />
          <label className={styles.questionLabel}>
            What are you seeking help for?
          </label>
          <p className={styles.questionDescription}>Select all that apply</p>
          <div className={styles.intentGrid}>
            {INTENT_OPTIONS.map((opt) => {
              const sel = selectedIntents.includes(opt.label);
              return (
                <button
                  key={opt.label}
                  className={`${styles.intentCard} ${sel ? styles.intentCardSelected : ''}`}
                  onClick={() => toggleIntent(opt.label)}
                >
                  {sel && <span className={styles.intentCardCheck}><Check /></span>}
                  <span className={styles.intentIcon}>{opt.icon}</span>
                  <span className={styles.intentLabel}>{opt.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      );
    }

    // Q2 — Lifestyle interest
    if (type === 'lifestyle') {
      return (
        <div className={styles.questionBlock}>
          <Counter />
          <label className={styles.questionLabel}>
            Are you interested in lifestyle guidance and personalized plans?
          </label>
          <ThreeRow field="lifestyleInterest" options={LIFESTYLE_OPTIONS} />
        </div>
      );
    }

    // Q3 — Hormone therapy
    if (type === 'hormone') {
      return (
        <div className={styles.questionBlock}>
          <Counter />
          <label className={styles.questionLabel}>
            Are you interested in hormone therapy consultation?
          </label>
          <ThreeRow field="hormoneInterest" options={HORMONE_OPTIONS} />
        </div>
      );
    }

    // Q4 — How to continue (chip grid)
    return (
      <div className={styles.questionBlock}>
        <Counter />
        <label className={styles.questionLabel}>How would you like to continue?</label>
        <div className={styles.chipGrid}>
          {CONTINUE_OPTIONS.map((opt) => {
            const sel = data.continueOption === opt;
            return (
              <button
                key={opt}
                className={`${styles.chip} ${sel ? styles.chipSelected : ''}`}
                onClick={() => onChange('continueOption', opt)}
              >
                {sel && <span className={styles.chipCheck}><Check /></span>}
                {opt}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // ── Render ───────────────────────────────────────────────
  return (
    <div className={styles.screenWrap}>
      <ScreenHeader onExit={onExit} />
      <ProgressBar step={11} total={11} />

      <div className={`${styles.screenBody} ${styles.scrollBody}`} ref={scrollRef}>

        {/* Title row */}
        <div className={styles.screenTitleRow}>
          <div>
            <h2 className={styles.screenTitle} style={{ textTransform: 'uppercase' }}>
              Care Intent
            </h2>
            <p className={styles.screenSubtitle}>
              Help us understand what you are looking for so we can recommend the right care and support.
            </p>
          </div>
          <div className={styles.screenIconBox} style={{ fontSize: 28 }}>💜</div>
        </div>

        {/* Active question */}
        {renderQuestion()}

        {/* Bottom spacer */}
        <div style={{ height: 16 }} />
      </div>

      {showButton && <ScrollToBottomButton onClick={scrollToBottom} />}

      {/* Footer nav */}
      <ScreenFooter
        onBack={handleBack}
        onNext={handleNext}
        nextLabel={isLastQuestion ? 'Review & Submit' : 'Continue'}
      />
    </div>
  );
};

export default MenopauseCareIntent;
